import React, { useState, useRef, useEffect, useMemo } from "react";
import Typography from "../../components/Typography/Typography";
import ReactMarkdown from "react-markdown";
import RelatedSearch from "../../components/RelatedSearch/RelatedSearch";
import ToolTip from "../../components/ToolTip/ToolTip";
import DocumentCard from "../../components/DocumentCard/DocumentCard";
import { CircleX } from "lucide-react";
import { gsap } from "gsap";
import animatedResponses from "./animatedResponses";

// DocumentReference component to handle hover interactions with documents
const DocumentReference = ({ num, response, index }) => {
  const dotRef = useRef(null);
  const [direction, setDirection] = useState("top");

  // Qui il nostro observer
  useEffect(() => {
    if (!dotRef.current) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        const viewportHeight = window.innerHeight;
        const dotTop = entry.boundingClientRect.top;

        if (dotTop < viewportHeight / 2) {
          setDirection("bottom");
        } else {
          setDirection("top");
        }
      },
      {
        root: null, // usa il viewport
        threshold: 0, // chiamato ogni volta che cambia
      }
    );

    observer.observe(dotRef.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  const docData = useMemo(() => {
    if (!response) return null;

    const allDocuments = [
      ...(response?.userDocument || response?.documents || []),
      ...(response?.userImages || response?.images || []),
      ...(response?.userUrls || response?.urls || []),
      ...(response?.semanticDocument || response?.semantic_scholar_results || [])
    ];

    const documentIndex = parseInt(num) - 1;
    const document = documentIndex >= 0 && documentIndex < allDocuments.length
      ? allDocuments[documentIndex]
      : null;

    if (!document) return null;

    const number = parseInt(num);
    const siteUrl = document.openAccessPdf?.url || document.siteUrl;

    let documentId;
    if (document.sk) {
      if (document.sk.startsWith("document#")) {
        documentId = document.sk.substring(9);
      } else if (document.sk.startsWith("image#")) {
        documentId = document.sk.substring(6);
      } else if (document.sk.startsWith("url#")) {
        const fullUrl = document.sk.substring(4);
        try {
          const urlObj = new URL(fullUrl);
          documentId = urlObj.hostname + urlObj.pathname;
        } catch (e) {
          documentId = fullUrl.split('?')[0];
        }
      } else {
        documentId = document.id || number;
      }
    } else {
      documentId = document.id || number;
    }

    return {
      id: documentId,
      number,
      title: document.title || "No title available",
      description: document.abstract || document.description || "Description not available",
      isScholar: !!document.authors,
      imageUrl: document.thumbnail_url || document.image_url || document.thumbnail || document.image,
      placeholder: !document.thumbnail_url && !document.image_url && !document.thumbnail && !document.image,
      siteUrl,
      year: document.year || "",
      journalName: document.journal?.name || "",
      hasPdf: !!document.openAccessPdf?.url
    };
  }, [response, num]);

  return (
    <ToolTip
      key={`tooltip-${index}`}
      direction={direction}
      text={
        docData ? (
          <div className="z-60">
            <DocumentCard {...docData} isRatio={false} showNumber={false} isHoverTooltip={true} />
          </div>
        ) : (
          <div className="flex items-center gap-2 text-gray-600">
            <CircleX size={16} />
            <Typography size="16" weight="sb" color="gray-600">
              Documento non trovato
            </Typography>
          </div>
        )
      }
    >
      <span
        ref={dotRef}
        className="cursor-pointer inline-flex items-center justify-center h-4 w-4 mx-1 bg-gray-200 rounded-full align-middle"
      >
        <Typography size="8" color="gray-600">
          {num}
        </Typography>
      </span>
    </ToolTip>
  );
};

// processSpecialFormatting completamente safe
const processSpecialFormatting = (content, response) => {
  if (!content) return content;

  if (typeof content === 'string') {
    const regex = /\{\{specialNumber:\s*((?:\d+(?:\s*,\s*\d+)*)+)\}\}/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = regex.exec(content)) !== null) {
      if (lastIndex < match.index) {
        parts.push(content.slice(lastIndex, match.index));
      }

      const numbersText = match[1];
      const numbers = numbersText.split(',').map(num => num.trim());

      const matchIndex = match.index;
      numbers.forEach((num, i) => {
        parts.push(
          <DocumentReference
            key={`ref-${matchIndex}-${i}`}
            num={num}
            response={response}
            index={`${matchIndex}-${i}`}
          />
        );
      });

      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts.length > 0 ? parts : content;
  }

  if (React.isValidElement(content)) {
    return React.cloneElement(content, {
      ...content.props,
      children: processSpecialFormatting(content.props.children, response)
    });
  }

  if (Array.isArray(content)) {
    return content.map((item, index) => processSpecialFormatting(item, response));
  }

  return content;
};

const AnimatedElement = ({ children, delay = 0, contentId, elementIndex, isLatestResponse }) => {
  const elementRef = useRef(null);
  const hasAnimatedRef = useRef(false);

  useEffect(() => {
    if (elementRef.current && !hasAnimatedRef.current && isLatestResponse && !animatedResponses.has(contentId)) {
      hasAnimatedRef.current = true;

      gsap.fromTo(
        elementRef.current,
        { opacity: 0, y: 20 },
        { opacity: 1, y: 0, duration: 0.6, delay: delay, ease: "power2.out" }
      );
    } else if (elementRef.current && !hasAnimatedRef.current) {
      hasAnimatedRef.current = true;
      elementRef.current.style.opacity = 1;
    }
  }, [delay, contentId, isLatestResponse]);

  return <div ref={elementRef} style={{ opacity: hasAnimatedRef.current ? 1 : 0 }}>{children}</div>;
};

// Main component
const ResearchContent = ({ textExample = {}, searchFunction, isLatestResponse = true, response }) => {
  const [isVisible, setIsVisible] = useState(true);
  const contentRef = useRef(null);
  const [markdownElements, setMarkdownElements] = useState([]);
  const [hasInitialized, setHasInitialized] = useState(false);
  const contentId = useRef(textExample?.id || `content-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`).current;

  useEffect(() => {
    if (textExample.content && !hasInitialized) {
      const elementTypeRegex = /^(#{1,6}|\*|\d+\.|>|\-|\+)\s|^\s*\n/gm;
      const matches = textExample.content.match(elementTypeRegex) || [];
      setMarkdownElements(matches);
      setHasInitialized(true);
    }

    return () => {
      if (isLatestResponse) {
        animatedResponses.add(contentId);
      }
    };
  }, [textExample.content, hasInitialized, contentId, isLatestResponse]);

  const handleSearch = (query) => {
    searchFunction(query);
  };

  let animationDelayCounter = 0;
  const shouldAnimate = isLatestResponse && !animatedResponses.has(contentId);

  return (
    <div ref={contentRef} className="flex flex-col gap-4 p-6 md:py-0 md:px-12 md:pb-0 pb-36">
      <ReactMarkdown
        components={{
          h1({ children }) {
            const delayValue = shouldAnimate ? 0.15 * animationDelayCounter++ : 0;
            return (
              <AnimatedElement delay={delayValue} contentId={contentId} isLatestResponse={isLatestResponse}>
                <h1 style={{ display: "inline-block" }}>
                  <Typography size="48" family="serif" weight="sb">
                    {processSpecialFormatting(children, response)}
                  </Typography>
                </h1>
              </AnimatedElement>
            );
          },
          h2({ children }) {
            const delayValue = shouldAnimate ? 0.15 * animationDelayCounter++ : 0;
            return (
              <AnimatedElement delay={delayValue} contentId={contentId} isLatestResponse={isLatestResponse}>
                <Typography size="20" weight="sb">
                  {processSpecialFormatting(children, response)}
                </Typography>
              </AnimatedElement>
            );
          },
          p({ children }) {
            const delayValue = shouldAnimate ? 0.15 * animationDelayCounter++ : 0;
            return (
              <AnimatedElement delay={delayValue} contentId={contentId} isLatestResponse={isLatestResponse}>
                <Typography size="16" weight="rg">
                  {processSpecialFormatting(children, response)}
                </Typography>
              </AnimatedElement>
            );
          },
          strong({ children }) {
            return (
              <Typography weight="sb">
                {processSpecialFormatting(children, response)}
              </Typography>
            );
          },
          li({ children }) {
            const delayValue = shouldAnimate ? 0.15 * animationDelayCounter++ : 0;
            return (
              <AnimatedElement delay={delayValue} contentId={contentId} isLatestResponse={isLatestResponse}>
                <li>
                  <Typography size="16" weight="rg">
                    {processSpecialFormatting(children, response)}
                  </Typography>
                </li>
              </AnimatedElement>
            );
          },
          ul({ children }) {
            return <ul className="list-disc pl-5 space-y-2">{children}</ul>;
          },
          ol({ children }) {
            return <ol className="list-decimal pl-5 space-y-2">{children}</ol>;
          },
        }}
      >
        {textExample.content}
      </ReactMarkdown>

      {isVisible && textExample.relatedSearches?.length > 0 && (
        <AnimatedElement delay={shouldAnimate ? 0.15 * animationDelayCounter : 0} contentId={contentId} isLatestResponse={isLatestResponse}>
          <div className="flex flex-col gap-4 mt-9">
            <Typography family="serif" size="32" weight="sb">
              Ricerche correlate
            </Typography>
            {textExample.relatedSearches.map((search, index) => (
              <div key={index}>
                <RelatedSearch text={search.text} onClick={() => handleSearch(search.text)} />
              </div>
            ))}
          </div>
        </AnimatedElement>
      )}
    </div>
  );
};

export default ResearchContent;
